/**
 * Strategy editor — Task 055.
 *
 * Structured editor for the V1 strategy content vocabulary documented
 * in `docs/frontend/050-strategy-content-contract.md`. Editing is
 * not in-place: clicking save POSTs to
 * `publish-client-strategy-version`, which delegates to the atomic
 * `public.publish_client_strategy_version_v1` RPC. That RPC inserts a
 * new immutable `strategy_versions` row and flips
 * `client_strategies.active_version_id` inside one transaction. The
 * old version stays in the table for history.
 *
 * Producer constraints honored:
 *   - Canonical V1 keys only (aliases get normalized in
 *     `fromContent` so the wire shape stays stable).
 *   - Sections with no real data still serialize, but the renderer
 *     hides empty cards.
 *   - Unknown keys from the original content (e.g. earlier
 *     `source_metadata`) are PRESERVED on save except `source_metadata`
 *     itself, which we overwrite to record the manual edit lineage.
 *
 * Out of scope: monthly_content_mix is V1 vocab but we omit it from
 * the structured editor because the template generator never emits it
 * and the agency adds it manually if needed. Anything in that key
 * round-trips untouched via the unknown-keys merge.
 */

import { useMemo, useState, type FormEvent } from 'react';
import { isApiError } from '../../api/client';
import { publishClientStrategyVersion, refineClientStrategySection } from '../../api/endpoints';
import {
  STRATEGY_SECTION_KEYS,
  type RefineClientStrategySectionPreviewData,
  type StrategySectionKey,
} from '../../api/types';
import { STRATEGY_CONTENT_V1_ALIASES } from '@kmi/contracts';
import { normalizeStrategyContent } from '../../api/strategyContent';

const TEMPLATE_PLACEHOLDER = 'يحتاج مراجعة من الفريق';
const SUMMARY_MAX = 2000;

/**
 * V1 sections the structured editor actually manages. Anything else in
 * the V1 vocabulary that lands in the wire content (currently only
 * `monthly_content_mix`) is round-tripped from the original content
 * untouched — silently dropping it on every save would be a
 * regression flagged by Codex P2 in round 1.
 */
const EDITOR_MANAGED_V1_SECTIONS: ReadonlyArray<string> = [
  'positioning',
  'value_proposition',
  'brand_values',
  'target_personas',
  'competitor_summary',
  'content_pillars',
  'tone_guidelines',
  'channel_recommendations',
  'roadmap',
  'kpi_targets',
  'dos_donts',
];

/**
 * Stable per-row key. React's default index-based keying causes
 * focus loss / DOM remount when an entry is deleted from the middle
 * of a repeatable list (the row that shifts down inherits the
 * deleted row's key, which forces a remount). We mint a UUID per
 * row at parse time and on every "add new" so the key is stable
 * across edits. Stripped on serialize.
 */
type WithKey<T> = T & { _key: string };

function mintKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `k-${Math.random().toString(36).slice(2)}-${Date.now().toString(36)}`;
}

interface EditableStrategy {
  positioning: { statement: string; audience: string; alternative: string };
  value_proposition: { headline: string; body: string; proof: string[] };
  brand_values: Array<WithKey<{ name: string; description: string }>>;
  target_personas: Array<
    WithKey<{
      name: string;
      demographic: string;
      psychographic: string;
      jobs_to_be_done: string[];
      pain_points: string[];
    }>
  >;
  competitor_summary: Array<
    WithKey<{
      name: string;
      positioning: string;
      strength: string;
      weakness: string;
    }>
  >;
  content_pillars: Array<WithKey<{ name: string; description: string; cadence: string }>>;
  tone_guidelines: { voice: string; tone: string[]; dos: string[]; donts: string[] };
  channel_recommendations: Array<
    WithKey<{
      channel: string;
      role: string;
      cadence: string;
      kpis: string[];
    }>
  >;
  roadmap: Array<WithKey<{ phase: string; period: string; focus: string; deliverables: string[] }>>;
  kpi_targets: Array<WithKey<{ metric: string; target: string; period: string }>>;
  dos_donts: { dos: string[]; donts: string[] };
}

export interface StrategyEditorProps {
  readonly strategyId: string;
  readonly currentContent: Readonly<Record<string, unknown>>;
  readonly currentSummary: string;
  readonly currentVersionId: string;
  readonly currentVersionNumber: number;
  readonly onCancel: () => void;
  readonly onSaved: () => void;
  /**
   * Task 065 — when true, the AI section-refine panel renders at the
   * top of the editor. The panel is independent of the manual edit
   * form; pre-publish it does not mutate editor fields. The parent
   * computes this from `me.permissions` (ai.run +
   * strategy.version.publish + not sysadmin) so the gate stays in
   * one place.
   */
  readonly canAiRefine?: boolean;
}

export function StrategyEditor({
  strategyId,
  currentContent,
  currentSummary,
  currentVersionId,
  currentVersionNumber,
  onCancel,
  onSaved,
  canAiRefine = false,
}: StrategyEditorProps) {
  const [state, setState] = useState<EditableStrategy>(() => fromContent(currentContent));
  const [summary, setSummary] = useState<string>(currentSummary);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const placeholderHits = useMemo(
    () => countPlaceholderOccurrences(state, summary),
    [state, summary],
  );

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    const trimmed = summary.trim();
    if (trimmed.length === 0) {
      setError('الملخّص لا يمكن أن يكون فارغاً.');
      return;
    }
    setSubmitting(true);
    try {
      const content = toContent(state, currentContent, currentVersionId, currentVersionNumber);
      await publishClientStrategyVersion({
        input: {
          strategy_id: strategyId,
          summary: trimmed,
          content,
        },
      });
      onSaved();
    } catch (err) {
      setSubmitting(false);
      if (isApiError(err)) {
        if (err.code === 'permission_denied') {
          setError('لا تملك صلاحية تعديل الاستراتيجية.');
          return;
        }
        if (err.code === 'conflict') {
          setError('تعديل آخر سبقك على نفس الاستراتيجية. حدّث الصفحة وأعد المحاولة.');
          return;
        }
        if (err.code === 'validation_error') {
          setError('بيانات التعديل غير صالحة. تأكد من الحقول وحاول مجدداً.');
          return;
        }
        if (err.code === 'not_found') {
          setError('الاستراتيجية غير متاحة أو لا تملك وصولاً إليها.');
          return;
        }
      }
      setError('تعذّر حفظ التعديلات. حاول مجدداً.');
    }
  }

  return (
    <form className="stack-md" onSubmit={onSubmit} noValidate>
      <div className="card" style={{ padding: 18 }}>
        <div className="sec-title">
          <h2>تعديل الاستراتيجية</h2>
          <span className="meta">من إصدار v{currentVersionNumber} → سيتم نشر إصدار جديد</span>
        </div>
        <p className="tiny" style={{ color: 'var(--muted)', marginTop: 6 }}>
          يتم إنشاء إصدار جديد عند الحفظ ويتم تثبيته كـ &laquo;الإصدار الفعّال&raquo;. لا يُستبدل
          الإصدار الحالي؛ يبقى محفوظاً في السجل التاريخي.
        </p>
        {placeholderHits > 0 ? (
          <div className="banner banner-info" role="status" style={{ marginTop: 10, fontSize: 12 }}>
            هذه الاستراتيجية ما تزال تحتوي على {placeholderHits} موضع
            {placeholderHits === 1 ? '' : ''} بعلامة &laquo;{TEMPLATE_PLACEHOLDER}&raquo;. استبدلها
            بمحتوى حقيقي قبل اعتماد الإصدار.
          </div>
        ) : null}
        {error ? (
          <div className="banner banner-error" role="alert" style={{ marginTop: 10 }}>
            {error}
          </div>
        ) : null}
      </div>

      {canAiRefine ? (
        <AiSectionRefinePanel
          strategyId={strategyId}
          currentVersionNumber={currentVersionNumber}
          onPublished={onSaved}
        />
      ) : null}

      <SectionCard title="الملخّص" eyebrow="summary">
        <TextArea
          value={summary}
          onChange={setSummary}
          placeholder="ملخص قصير يلخص نقاط الاستراتيجية الأساسية"
          rows={3}
          maxLength={SUMMARY_MAX}
        />
        <p className="tiny" style={{ color: 'var(--muted)', margin: 0 }}>
          ٢-٣ جمل. يُعرض في رأس مستند الاستراتيجية.
        </p>
      </SectionCard>

      <SectionCard title="التموضع" eyebrow="positioning">
        <Field label="بيان التموضع">
          <TextArea
            value={state.positioning.statement}
            onChange={(v) =>
              setState((s) => ({ ...s, positioning: { ...s.positioning, statement: v } }))
            }
            rows={2}
          />
        </Field>
        <Field label="الجمهور">
          <TextField
            value={state.positioning.audience}
            onChange={(v) =>
              setState((s) => ({ ...s, positioning: { ...s.positioning, audience: v } }))
            }
          />
        </Field>
        <Field label="البديل المرفوض">
          <TextField
            value={state.positioning.alternative}
            onChange={(v) =>
              setState((s) => ({ ...s, positioning: { ...s.positioning, alternative: v } }))
            }
          />
        </Field>
      </SectionCard>

      <SectionCard title="عرض القيمة" eyebrow="value_proposition">
        <Field label="العنوان">
          <TextField
            value={state.value_proposition.headline}
            onChange={(v) =>
              setState((s) => ({
                ...s,
                value_proposition: { ...s.value_proposition, headline: v },
              }))
            }
          />
        </Field>
        <Field label="الجسم">
          <TextArea
            value={state.value_proposition.body}
            onChange={(v) =>
              setState((s) => ({ ...s, value_proposition: { ...s.value_proposition, body: v } }))
            }
            rows={3}
          />
        </Field>
        <Field label="نقاط الإثبات">
          <StringList
            items={state.value_proposition.proof}
            onChange={(items) =>
              setState((s) => ({
                ...s,
                value_proposition: { ...s.value_proposition, proof: items },
              }))
            }
            itemLabel="نقطة إثبات"
          />
        </Field>
      </SectionCard>

      <SectionCard title="القيم الأساسية" eyebrow="brand_values">
        <RepeatableList
          items={state.brand_values}
          onChange={(items) => setState((s) => ({ ...s, brand_values: items }))}
          template={() => ({ _key: mintKey(), name: '', description: '' })}
          itemLabel="قيمة"
          render={(item, update) => (
            <>
              <Field label="الاسم">
                <TextField value={item.name} onChange={(v) => update({ ...item, name: v })} />
              </Field>
              <Field label="الوصف">
                <TextField
                  value={item.description}
                  onChange={(v) => update({ ...item, description: v })}
                />
              </Field>
            </>
          )}
        />
      </SectionCard>

      <SectionCard title="شخصيات الجمهور" eyebrow="target_personas">
        <RepeatableList
          items={state.target_personas}
          onChange={(items) => setState((s) => ({ ...s, target_personas: items }))}
          template={() => ({
            _key: mintKey(),
            name: '',
            demographic: '',
            psychographic: '',
            jobs_to_be_done: [],
            pain_points: [],
          })}
          itemLabel="شخصية"
          render={(item, update) => (
            <>
              <Field label="الاسم">
                <TextField value={item.name} onChange={(v) => update({ ...item, name: v })} />
              </Field>
              <Field label="الوصف الديموغرافي">
                <TextField
                  value={item.demographic}
                  onChange={(v) => update({ ...item, demographic: v })}
                />
              </Field>
              <Field label="الدوافع/الخصائص النفسية">
                <TextArea
                  value={item.psychographic}
                  onChange={(v) => update({ ...item, psychographic: v })}
                  rows={2}
                />
              </Field>
              <Field label="المهام (Jobs to be done)">
                <StringList
                  items={item.jobs_to_be_done}
                  onChange={(arr) => update({ ...item, jobs_to_be_done: arr })}
                  itemLabel="مهمة"
                />
              </Field>
              <Field label="نقاط الألم">
                <StringList
                  items={item.pain_points}
                  onChange={(arr) => update({ ...item, pain_points: arr })}
                  itemLabel="نقطة ألم"
                />
              </Field>
            </>
          )}
        />
      </SectionCard>

      <SectionCard title="ملخص المنافسين" eyebrow="competitor_summary">
        <RepeatableList
          items={state.competitor_summary}
          onChange={(items) => setState((s) => ({ ...s, competitor_summary: items }))}
          template={() => ({
            _key: mintKey(),
            name: '',
            positioning: '',
            strength: '',
            weakness: '',
          })}
          itemLabel="منافس"
          render={(item, update) => (
            <>
              <Field label="الاسم">
                <TextField value={item.name} onChange={(v) => update({ ...item, name: v })} />
              </Field>
              <Field label="التموضع">
                <TextField
                  value={item.positioning}
                  onChange={(v) => update({ ...item, positioning: v })}
                />
              </Field>
              <Field label="نقطة القوّة">
                <TextField
                  value={item.strength}
                  onChange={(v) => update({ ...item, strength: v })}
                />
              </Field>
              <Field label="نقطة الضعف">
                <TextField
                  value={item.weakness}
                  onChange={(v) => update({ ...item, weakness: v })}
                />
              </Field>
            </>
          )}
        />
      </SectionCard>

      <SectionCard title="أعمدة المحتوى" eyebrow="content_pillars">
        <RepeatableList
          items={state.content_pillars}
          onChange={(items) => setState((s) => ({ ...s, content_pillars: items }))}
          template={() => ({ _key: mintKey(), name: '', description: '', cadence: '' })}
          itemLabel="عمود"
          render={(item, update) => (
            <>
              <Field label="الاسم">
                <TextField value={item.name} onChange={(v) => update({ ...item, name: v })} />
              </Field>
              <Field label="الوصف">
                <TextField
                  value={item.description}
                  onChange={(v) => update({ ...item, description: v })}
                />
              </Field>
              <Field label="الوتيرة">
                <TextField
                  value={item.cadence}
                  onChange={(v) => update({ ...item, cadence: v })}
                  placeholder="أسبوعي / شهري / لكل حملة"
                />
              </Field>
            </>
          )}
        />
      </SectionCard>

      <SectionCard title="نبرة التواصل" eyebrow="tone_guidelines">
        <Field label="الصوت">
          <TextField
            value={state.tone_guidelines.voice}
            onChange={(v) =>
              setState((s) => ({ ...s, tone_guidelines: { ...s.tone_guidelines, voice: v } }))
            }
          />
        </Field>
        <Field label="النبرة (وسوم)">
          <StringList
            items={state.tone_guidelines.tone}
            onChange={(arr) =>
              setState((s) => ({ ...s, tone_guidelines: { ...s.tone_guidelines, tone: arr } }))
            }
            itemLabel="نبرة"
          />
        </Field>
        <Field label="افعل">
          <StringList
            items={state.tone_guidelines.dos}
            onChange={(arr) =>
              setState((s) => ({ ...s, tone_guidelines: { ...s.tone_guidelines, dos: arr } }))
            }
            itemLabel="افعل"
          />
        </Field>
        <Field label="تجنّب">
          <StringList
            items={state.tone_guidelines.donts}
            onChange={(arr) =>
              setState((s) => ({ ...s, tone_guidelines: { ...s.tone_guidelines, donts: arr } }))
            }
            itemLabel="تجنّب"
          />
        </Field>
      </SectionCard>

      <SectionCard title="القنوات" eyebrow="channel_recommendations">
        <RepeatableList
          items={state.channel_recommendations}
          onChange={(items) => setState((s) => ({ ...s, channel_recommendations: items }))}
          template={() => ({
            _key: mintKey(),
            channel: '',
            role: '',
            cadence: '',
            kpis: [],
          })}
          itemLabel="قناة"
          render={(item, update) => (
            <>
              <Field label="القناة">
                <TextField
                  value={item.channel}
                  onChange={(v) => update({ ...item, channel: v })}
                  placeholder="Instagram / TikTok / Snapchat"
                />
              </Field>
              <Field label="الدور">
                <TextField value={item.role} onChange={(v) => update({ ...item, role: v })} />
              </Field>
              <Field label="الوتيرة">
                <TextField value={item.cadence} onChange={(v) => update({ ...item, cadence: v })} />
              </Field>
              <Field label="مؤشرات الأداء">
                <StringList
                  items={item.kpis}
                  onChange={(arr) => update({ ...item, kpis: arr })}
                  itemLabel="KPI"
                />
              </Field>
            </>
          )}
        />
      </SectionCard>

      <SectionCard title="خارطة التنفيذ" eyebrow="roadmap">
        <RepeatableList
          items={state.roadmap}
          onChange={(items) => setState((s) => ({ ...s, roadmap: items }))}
          template={() => ({
            _key: mintKey(),
            phase: '',
            period: '',
            focus: '',
            deliverables: [],
          })}
          itemLabel="مرحلة"
          render={(item, update) => (
            <>
              <Field label="المرحلة">
                <TextField value={item.phase} onChange={(v) => update({ ...item, phase: v })} />
              </Field>
              <Field label="الفترة">
                <TextField value={item.period} onChange={(v) => update({ ...item, period: v })} />
              </Field>
              <Field label="التركيز">
                <TextArea
                  value={item.focus}
                  onChange={(v) => update({ ...item, focus: v })}
                  rows={2}
                />
              </Field>
              <Field label="التسليمات">
                <StringList
                  items={item.deliverables}
                  onChange={(arr) => update({ ...item, deliverables: arr })}
                  itemLabel="تسليم"
                />
              </Field>
            </>
          )}
        />
      </SectionCard>

      <SectionCard title="مؤشرات الأداء المستهدفة" eyebrow="kpi_targets">
        <RepeatableList
          items={state.kpi_targets}
          onChange={(items) => setState((s) => ({ ...s, kpi_targets: items }))}
          template={() => ({ _key: mintKey(), metric: '', target: '', period: '' })}
          itemLabel="مؤشر"
          render={(item, update) => (
            <>
              <Field label="المؤشر">
                <TextField value={item.metric} onChange={(v) => update({ ...item, metric: v })} />
              </Field>
              <Field label="الهدف">
                <TextField value={item.target} onChange={(v) => update({ ...item, target: v })} />
              </Field>
              <Field label="الفترة">
                <TextField value={item.period} onChange={(v) => update({ ...item, period: v })} />
              </Field>
            </>
          )}
        />
      </SectionCard>

      <SectionCard title="افعل / تجنّب" eyebrow="dos_donts">
        <Field label="افعل">
          <StringList
            items={state.dos_donts.dos}
            onChange={(arr) => setState((s) => ({ ...s, dos_donts: { ...s.dos_donts, dos: arr } }))}
            itemLabel="افعل"
          />
        </Field>
        <Field label="تجنّب">
          <StringList
            items={state.dos_donts.donts}
            onChange={(arr) =>
              setState((s) => ({ ...s, dos_donts: { ...s.dos_donts, donts: arr } }))
            }
            itemLabel="تجنّب"
          />
        </Field>
      </SectionCard>

      <div
        className="card"
        style={{
          padding: 14,
          display: 'flex',
          gap: 10,
          justifyContent: 'flex-end',
          flexWrap: 'wrap',
        }}
      >
        <button
          type="button"
          className="btn-text"
          onClick={onCancel}
          disabled={submitting}
          style={{ padding: '8px 12px' }}
        >
          إلغاء
        </button>
        <button type="submit" className="auth-cta" disabled={submitting} style={{ width: 'auto' }}>
          {submitting ? <span className="spinner" /> : null}
          <span>{submitting ? 'جاري النشر…' : 'نشر إصدار جديد'}</span>
        </button>
      </div>
    </form>
  );
}

// ─── Task 065 — AI section refine panel ──────────────────────────────

const SECTION_LABEL_AR: { readonly [K in StrategySectionKey]: string } = {
  positioning: 'التموضع',
  value_proposition: 'عرض القيمة',
  brand_values: 'القيم الأساسية',
  target_personas: 'شخصيات الجمهور',
  competitor_summary: 'ملخص المنافسين',
  content_pillars: 'أعمدة المحتوى',
  tone_guidelines: 'نبرة التواصل',
  channel_recommendations: 'القنوات',
  monthly_content_mix: 'توزيع المحتوى الشهري',
  roadmap: 'خارطة التنفيذ',
  kpi_targets: 'مؤشرات الأداء المستهدفة',
  dos_donts: 'افعل / تجنّب',
};

const INSTRUCTION_MAX = 2_000;

/**
 * Section-level AI refine panel for Task 065. Renders ONLY when the
 * parent passes `canAiRefine={true}` (gate: ai.run +
 * strategy.version.publish + not sysadmin). Independent of the
 * manual editor's form state — preview shows the AI's proposed
 * section read-only, and publish goes straight to the publish RPC
 * via the edge function. Does NOT mutate the surrounding editor
 * fields under any code path.
 *
 * UX flow:
 *   1. user picks a section
 *   2. user writes an instruction
 *   3. user clicks "معاينة" — call edge function in `preview` mode
 *   4. read-only preview renders + warnings + confidence + cost
 *   5. user clicks "نشر إصدار جديد" — call edge function in
 *      `publish` mode → parent reloads on success
 *   6. user clicks "تجاهل" to discard the preview
 *
 * No auto-apply. No silent mutation. Every state change is
 * explicitly user-initiated.
 */
function AiSectionRefinePanel({
  strategyId,
  currentVersionNumber,
  onPublished,
}: {
  readonly strategyId: string;
  readonly currentVersionNumber: number;
  readonly onPublished: () => void;
}) {
  const [sectionKey, setSectionKey] = useState<StrategySectionKey>('positioning');
  const [instruction, setInstruction] = useState<string>('');
  // Preview data stored verbatim. Publish sends this back unchanged
  // so the user always publishes the EXACT section they reviewed —
  // see the Task 065 hardening: the publish endpoint never re-runs
  // the AI worker.
  const [preview, setPreview] = useState<RefineClientStrategySectionPreviewData | null>(null);
  const [busy, setBusy] = useState<'preview' | 'publish' | null>(null);
  const [error, setError] = useState<string | null>(null);

  function resetPreview() {
    setPreview(null);
    setError(null);
  }

  async function onPreview() {
    if (busy) return;
    const trimmed = instruction.trim();
    if (trimmed.length === 0) {
      setError('اكتب تعليمات التحسين قبل المعاينة.');
      return;
    }
    if (trimmed.length > INSTRUCTION_MAX) {
      setError(`التعليمات طويلة جداً (الحد الأقصى ${INSTRUCTION_MAX} حرف).`);
      return;
    }
    setError(null);
    setBusy('preview');
    try {
      const result = await refineClientStrategySection({
        input: {
          mode: 'preview',
          strategy_id: strategyId,
          section_key: sectionKey,
          instruction: trimmed,
        },
      });
      setPreview(result);
    } catch (err) {
      setBusy(null);
      setError(translateError(err));
      return;
    }
    setBusy(null);
  }

  async function onPublish() {
    if (busy || !preview) return;
    if (
      !window.confirm(
        'سيتم نشر إصدار جديد يحتوي على القسم المحسّن فقط. التعديلات اليدوية غير المحفوظة في المحرّر ستُفقد بعد إعادة التحميل. متابعة؟',
      )
    ) {
      return;
    }
    setError(null);
    setBusy('publish');
    try {
      // Publish does NOT re-call the AI. We submit the preview
      // payload exactly as received so the persisted version
      // contains the section the user reviewed. The server
      // re-validates the shape, verifies the preview ai_run_id
      // belongs to this caller, and rejects with 409 if a parallel
      // publish has advanced the strategy past the preview's base
      // version.
      await refineClientStrategySection({
        input: {
          mode: 'publish',
          strategy_id: strategyId,
          section_key: preview.section_key,
          refined_section: preview.refined_section,
          base_version_id: preview.base_version_id,
          base_version_number: preview.base_version_number,
          preview_ai_run_id: preview.ai_run_id,
          preview_model: preview.model,
          preview_cost_usd: preview.cost_usd,
          preview_tokens_input: preview.tokens_input,
          preview_tokens_output: preview.tokens_output,
          preview_duration_ms: preview.duration_ms,
          preview_confidence_score: preview.confidence_score,
          preview_placeholders: preview.placeholders,
          instruction_hash: preview.instruction_hash,
          instruction_excerpt: preview.instruction_excerpt,
          instruction_length: preview.instruction_length,
          refined_section_hash: preview.refined_section_hash,
        },
      });
      onPublished();
    } catch (err) {
      setBusy(null);
      // On a stale-base 409, drop the preview so the user is
      // forced to regenerate against the new active version
      // rather than retrying with the same now-invalid payload.
      if (isApiError(err) && err.code === 'conflict') {
        setPreview(null);
      }
      setError(translateError(err));
    }
  }

  const lowConfidence = preview ? preview.confidence_score < 0.5 : false;

  return (
    <section
      className="card"
      style={{ padding: 18, borderColor: 'var(--ai-accent, var(--brand))' }}
    >
      <div className="sec-title">
        <h3 style={{ margin: 0, fontSize: 14 }}>تحسين قسم بالذكاء الاصطناعي</h3>
        <span className="meta">section_refine</span>
      </div>
      <p className="tiny" style={{ color: 'var(--muted)', marginTop: 6 }}>
        اختر قسماً واكتب تعليمات التحسين. سيتم تشغيل الذكاء الاصطناعي ضد بيانات التأهيل المعتمدة
        فقط. سترى المعاينة هنا قبل اتخاذ قرار النشر — لا يتم تطبيق أي تغيير تلقائياً.
      </p>

      {error ? (
        <div className="banner banner-error" role="alert" style={{ marginTop: 10 }}>
          {error}
        </div>
      ) : null}

      <div className="stack-sm" style={{ marginTop: 12 }}>
        <Field label="القسم">
          <select
            className="auth-input"
            value={sectionKey}
            disabled={busy !== null || preview !== null}
            onChange={(e) => {
              const next = e.currentTarget.value as StrategySectionKey;
              if (STRATEGY_SECTION_KEYS.includes(next)) {
                setSectionKey(next);
                setPreview(null);
              }
            }}
            style={{ paddingInlineStart: 14 }}
          >
            {STRATEGY_SECTION_KEYS.map((k) => (
              <option key={k} value={k}>
                {SECTION_LABEL_AR[k]} ({k})
              </option>
            ))}
          </select>
        </Field>
        <Field label="تعليمات التحسين">
          <TextArea
            value={instruction}
            onChange={(v) => {
              setInstruction(v);
              if (preview) setPreview(null);
            }}
            placeholder="مثال: اجعل عرض القيمة أكثر تركيزاً على عملاء B2B في الرياض، واذكر السرعة كميزة أساسية."
            rows={3}
            maxLength={INSTRUCTION_MAX}
          />
          <p className="tiny" style={{ color: 'var(--muted)', margin: 0 }}>
            حد أقصى {INSTRUCTION_MAX.toLocaleString('en-US')} حرف. تُسجَّل بصمة (hash) من التعليمات
            ضمن سجل الإصدار، دون النص الكامل.
          </p>
        </Field>
      </div>

      <div
        style={{
          display: 'flex',
          gap: 10,
          justifyContent: 'flex-end',
          flexWrap: 'wrap',
          marginTop: 14,
        }}
      >
        {preview ? (
          <button
            type="button"
            className="btn-text"
            onClick={resetPreview}
            disabled={busy !== null}
            style={{ padding: '8px 12px' }}
          >
            تجاهل المعاينة
          </button>
        ) : null}
        {!preview ? (
          <button
            type="button"
            className="auth-cta"
            onClick={onPreview}
            disabled={busy !== null || instruction.trim().length === 0}
            style={{ width: 'auto' }}
          >
            {busy === 'preview' ? <span className="spinner" /> : null}
            <span>{busy === 'preview' ? 'جاري المعاينة…' : 'معاينة'}</span>
          </button>
        ) : (
          <button
            type="button"
            className="auth-cta"
            onClick={onPublish}
            disabled={busy !== null}
            style={{ width: 'auto' }}
          >
            {busy === 'publish' ? <span className="spinner" /> : null}
            <span>{busy === 'publish' ? 'جاري النشر…' : 'نشر إصدار جديد'}</span>
          </button>
        )}
      </div>

      {preview ? (
        <div
          className={`banner ${lowConfidence ? 'banner-error' : 'banner-info'}`}
          role="status"
          style={{ marginTop: 14 }}
        >
          <strong>المعاينة جاهزة — راجع النتيجة قبل النشر.</strong>
          <div className="tiny" style={{ marginTop: 6 }}>
            ثقة النموذج: {Math.round(preview.confidence_score * 100)}%
            {lowConfidence ? ' — منخفضة، راجع المحتوى بعناية قبل الاعتماد.' : ''} ·{' '}
            {preview.warnings.length > 0 ? `${preview.warnings.length} تنبيه` : 'بدون تنبيهات'} ·{' '}
            {preview.placeholders.length > 0
              ? `${preview.placeholders.length} حقل فارغ`
              : 'لا حقول فارغة'}{' '}
            · تكلفة التشغيل: ${preview.cost_usd.toFixed(4)}
          </div>
        </div>
      ) : null}

      {preview ? (
        <div className="stack-sm" style={{ marginTop: 14 }}>
          <div className="sec-title" style={{ alignItems: 'baseline' }}>
            <h4 style={{ margin: 0, fontSize: 13 }}>القسم بعد التحسين</h4>
            <span className="meta">{SECTION_LABEL_AR[preview.section_key]}</span>
          </div>
          <pre
            style={{
              background: 'var(--paper)',
              padding: 12,
              borderRadius: 'var(--r-md)',
              border: '1px solid var(--line)',
              fontSize: 12,
              direction: 'ltr',
              textAlign: 'left',
              maxHeight: 480,
              overflow: 'auto',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {JSON.stringify(preview.refined_section, null, 2)}
          </pre>
          {preview.warnings.length > 0 ? (
            <div className="stack-sm">
              <h5 style={{ margin: 0, fontSize: 12, color: 'var(--muted)' }}>تنبيهات النموذج</h5>
              <ul className="tiny" style={{ paddingInlineStart: 18, margin: 0 }}>
                {preview.warnings.map((w, idx) => (
                  <li key={idx}>
                    <strong>[{w.code}]</strong> {w.message}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          {preview.placeholders.length > 0 ? (
            <div className="stack-sm">
              <h5 style={{ margin: 0, fontSize: 12, color: 'var(--muted)' }}>
                حقول تركها النموذج فارغة
              </h5>
              <ul className="tiny" style={{ paddingInlineStart: 18, margin: 0 }}>
                {preview.placeholders.map((p, idx) => (
                  <li key={idx}>{p}</li>
                ))}
              </ul>
            </div>
          ) : null}
          {preview.summary_delta ? (
            <p className="tiny" style={{ color: 'var(--muted)', margin: 0 }}>
              <strong>اقتراح تحديث الملخّص:</strong> {preview.summary_delta} (يُعرض للمراجعة فقط؛ لا
              يُنشر مع تحسين القسم في V1.)
            </p>
          ) : null}
          <p className="tiny" style={{ color: 'var(--muted)', margin: 0 }}>
            عند النشر، سيتم إنشاء إصدار جديد مبني على الإصدار الحالي v{currentVersionNumber}، ويتم
            استبدال هذا القسم فقط. باقي الأقسام تبقى كما هي تماماً.
          </p>
        </div>
      ) : null}
    </section>
  );
}

function translateError(err: unknown): string {
  if (isApiError(err)) {
    if (err.code === 'permission_denied') {
      return 'لا تملك صلاحية تحسين الاستراتيجية بالذكاء الاصطناعي.';
    }
    if (err.code === 'not_found') {
      return 'الاستراتيجية غير متاحة أو لا تملك وصولاً إليها.';
    }
    if (err.code === 'validation_error') {
      return 'بيانات الطلب غير صالحة. تحقق من القسم والتعليمات وحاول مجدداً.';
    }
    if (err.code === 'conflict') {
      // Preview-stale-vs-active conflict OR a publish-RPC race on
      // the version-number unique index. Either way the user needs
      // to regenerate the preview; the existing instruction stays
      // on screen.
      return 'تم تحديث الاستراتيجية بعد المعاينة. أعد توليد المعاينة قبل النشر.';
    }
    if (err.code === 'unauthenticated') {
      return 'انتهت الجلسة. أعد تسجيل الدخول وحاول مجدداً.';
    }
  }
  return 'تعذّر تشغيل الذكاء الاصطناعي. حاول مجدداً.';
}

// ─── helpers (UI primitives) ─────────────────────────────────────────

function SectionCard({
  title,
  eyebrow,
  children,
}: {
  readonly title: string;
  readonly eyebrow: string;
  readonly children: React.ReactNode;
}) {
  return (
    <section className="card" style={{ padding: 18 }}>
      <div className="sec-title">
        <h3 style={{ margin: 0, fontSize: 14 }}>{title}</h3>
        <span className="meta">{eyebrow}</span>
      </div>
      <div className="stack-sm" style={{ marginTop: 10 }}>
        {children}
      </div>
    </section>
  );
}

function Field({
  label,
  children,
}: {
  readonly label: string;
  readonly children: React.ReactNode;
}) {
  return (
    <label className="auth-field" style={{ marginBottom: 0, display: 'block' }}>
      <span className="auth-field-label">{label}</span>
      {children}
    </label>
  );
}

function TextField({
  value,
  onChange,
  placeholder,
}: {
  readonly value: string;
  readonly onChange: (v: string) => void;
  readonly placeholder?: string;
}) {
  return (
    <input
      type="text"
      className="auth-input"
      value={value}
      onChange={(e) => onChange(e.currentTarget.value)}
      placeholder={placeholder}
      style={{ paddingInlineStart: 14 }}
    />
  );
}

function TextArea({
  value,
  onChange,
  placeholder,
  rows,
  maxLength,
}: {
  readonly value: string;
  readonly onChange: (v: string) => void;
  readonly placeholder?: string;
  readonly rows?: number;
  readonly maxLength?: number;
}) {
  return (
    <textarea
      className="auth-input"
      value={value}
      onChange={(e) => onChange(e.currentTarget.value)}
      placeholder={placeholder}
      rows={rows ?? 2}
      maxLength={maxLength}
      style={{ paddingInlineStart: 14, resize: 'vertical' }}
    />
  );
}

function StringList({
  items,
  onChange,
  itemLabel,
}: {
  readonly items: ReadonlyArray<string>;
  readonly onChange: (next: string[]) => void;
  readonly itemLabel: string;
}) {
  return (
    <div className="stack-sm">
      {items.map((value, idx) => (
        <div key={idx} style={{ display: 'flex', gap: 8 }}>
          <input
            type="text"
            className="auth-input"
            value={value}
            onChange={(e) => {
              const next = items.slice();
              next[idx] = e.currentTarget.value;
              onChange(next);
            }}
            placeholder={`${itemLabel} #${idx + 1}`}
            style={{ paddingInlineStart: 14, flex: 1 }}
          />
          <button
            type="button"
            className="btn-text"
            onClick={() => {
              const next = items.slice();
              next.splice(idx, 1);
              onChange(next);
            }}
            style={{ padding: '6px 10px', fontSize: 12 }}
          >
            حذف
          </button>
        </div>
      ))}
      <button
        type="button"
        className="btn-text"
        onClick={() => onChange([...items, ''])}
        style={{ padding: '6px 10px', fontSize: 12, alignSelf: 'flex-start' }}
      >
        + إضافة {itemLabel}
      </button>
    </div>
  );
}

function RepeatableList<T extends { _key: string }>({
  items,
  onChange,
  template,
  itemLabel,
  render,
}: {
  readonly items: ReadonlyArray<T>;
  readonly onChange: (next: T[]) => void;
  readonly template: () => T;
  readonly itemLabel: string;
  readonly render: (item: T, update: (next: T) => void) => React.ReactNode;
}) {
  return (
    <div className="stack-sm">
      {items.map((item, idx) => (
        <div
          key={item._key}
          style={{
            padding: 12,
            border: '1px solid var(--line)',
            borderRadius: 'var(--r-md)',
            background: 'var(--paper)',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 8,
            }}
          >
            <span className="lbl">
              {itemLabel} #{idx + 1}
            </span>
            <button
              type="button"
              className="btn-text"
              onClick={() => {
                const next = items.slice();
                next.splice(idx, 1);
                onChange(next);
              }}
              style={{ padding: '4px 8px', fontSize: 11 }}
            >
              حذف
            </button>
          </div>
          <div className="stack-sm">
            {render(item, (nextItem) => {
              const next = items.slice();
              next[idx] = nextItem;
              onChange(next);
            })}
          </div>
        </div>
      ))}
      <button
        type="button"
        className="btn-text"
        onClick={() => onChange([...items, template()])}
        style={{ padding: '6px 10px', fontSize: 12, alignSelf: 'flex-start' }}
      >
        + إضافة {itemLabel}
      </button>
    </div>
  );
}

// ─── content (de)serialization ───────────────────────────────────────

/**
 * Build the editable shape from a strategy_versions.content payload.
 * Uses the existing V1 normalizer to handle aliases (e.g. `personas`
 * → `target_personas`). Empty arrays / null fields collapse to empty
 * strings / [] in editor state for simpler input binding; the
 * serializer reverses that when needed.
 */
function fromContent(content: Readonly<Record<string, unknown>>): EditableStrategy {
  const r = normalizeStrategyContent(content);
  return {
    positioning: {
      statement: r.positioning?.statement ?? '',
      audience: r.positioning?.audience ?? '',
      alternative: r.positioning?.alternative ?? '',
    },
    value_proposition: {
      headline: r.value_proposition?.headline ?? '',
      body: r.value_proposition?.body ?? '',
      proof: [...(r.value_proposition?.proof ?? [])],
    },
    brand_values: r.brand_values.map((v) => ({
      _key: mintKey(),
      name: v.name,
      description: v.description ?? '',
    })),
    target_personas: r.target_personas.map((p) => ({
      _key: mintKey(),
      name: p.name,
      demographic: p.demographic ?? '',
      psychographic: p.psychographic ?? '',
      jobs_to_be_done: [...p.jobs_to_be_done],
      pain_points: [...p.pain_points],
    })),
    competitor_summary: r.competitor_summary.map((c) => ({
      _key: mintKey(),
      name: c.name,
      positioning: c.positioning ?? '',
      strength: c.strength ?? '',
      weakness: c.weakness ?? '',
    })),
    content_pillars: r.content_pillars.map((p) => ({
      _key: mintKey(),
      name: p.name,
      description: p.description ?? '',
      cadence: p.cadence ?? '',
    })),
    tone_guidelines: {
      voice: r.tone_guidelines?.voice ?? '',
      tone: [...(r.tone_guidelines?.tone ?? [])],
      dos: [...(r.tone_guidelines?.dos ?? [])],
      donts: [...(r.tone_guidelines?.donts ?? [])],
    },
    channel_recommendations: r.channel_recommendations.map((c) => ({
      _key: mintKey(),
      channel: c.channel,
      role: c.role ?? '',
      cadence: c.cadence ?? '',
      kpis: [...c.kpis],
    })),
    roadmap: r.roadmap.map((rm) => ({
      _key: mintKey(),
      phase: rm.phase,
      period: rm.period ?? '',
      focus: rm.focus ?? '',
      deliverables: [...rm.deliverables],
    })),
    kpi_targets: r.kpi_targets.map((k) => ({
      _key: mintKey(),
      metric: k.metric,
      target: k.target ?? '',
      period: k.period ?? '',
    })),
    dos_donts: {
      dos: [...(r.dos_donts?.dos ?? [])],
      donts: [...(r.dos_donts?.donts ?? [])],
    },
  };
}

/**
 * Build a wire-shape jsonb from edited state, preserving any unknown
 * keys from the original `content`. The canonical V1 keys we manage
 * here OVERWRITE whatever the original had under those keys (or under
 * their aliases). Aliases never round-trip — the editor canonicalises
 * to the V1 vocabulary.
 *
 * `source_metadata` is the one preserved-but-rewritten key: we attach
 * an `edited_*` block that records the manual-edit lineage (base
 * version id + number, edited_at). Earlier `generated_by` /
 * `template_version` / etc. stay nested under `source_metadata.base`
 * so future audits can walk the chain.
 */
function toContent(
  state: EditableStrategy,
  original: Readonly<Record<string, unknown>>,
  baseVersionId: string,
  baseVersionNumber: number,
): Record<string, unknown> {
  // Only strip V1 keys we MANAGE — that way `monthly_content_mix`
  // (and any future V1 section the editor doesn't cover) round-trips
  // from the original untouched instead of being silently dropped on
  // every save.
  //
  // Aliases get the same treatment: strip ONLY aliases whose
  // canonical target is in `EDITOR_MANAGED_V1_SECTIONS`. An alias
  // pointing at a non-managed canonical (e.g. `content_mix` →
  // `monthly_content_mix`) must round-trip — otherwise a payload
  // using the alias form gets silently dropped while the canonical
  // form survives, which is the second-order data-loss bug Codex
  // P2 (round 2) flagged.
  const managedKeys = new Set<string>(EDITOR_MANAGED_V1_SECTIONS);
  const managedAliasKeys = new Set<string>(
    Object.entries(STRATEGY_CONTENT_V1_ALIASES)
      .filter(([, canonical]) => managedKeys.has(canonical))
      .map(([alias]) => alias),
  );

  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(original)) {
    if (key === 'source_metadata') continue;
    if (managedKeys.has(key)) continue;
    if (managedAliasKeys.has(key)) continue;
    out[key] = value;
  }

  out.positioning = stripEmptyObject(state.positioning);
  out.value_proposition = {
    ...(state.value_proposition.headline ? { headline: state.value_proposition.headline } : {}),
    ...(state.value_proposition.body ? { body: state.value_proposition.body } : {}),
    proof: stripEmptyStrings(state.value_proposition.proof),
  };
  out.brand_values = state.brand_values
    .filter((v) => v.name.trim().length > 0)
    .map((v) =>
      v.description.trim().length > 0
        ? { name: v.name.trim(), description: v.description.trim() }
        : { name: v.name.trim() },
    );
  out.target_personas = state.target_personas
    .filter((p) => p.name.trim().length > 0)
    .map((p) => ({
      name: p.name.trim(),
      ...(p.demographic.trim() ? { demographic: p.demographic.trim() } : {}),
      ...(p.psychographic.trim() ? { psychographic: p.psychographic.trim() } : {}),
      jobs_to_be_done: stripEmptyStrings(p.jobs_to_be_done),
      pain_points: stripEmptyStrings(p.pain_points),
    }));
  out.competitor_summary = state.competitor_summary
    .filter((c) => c.name.trim().length > 0)
    .map((c) => ({
      name: c.name.trim(),
      ...(c.positioning.trim() ? { positioning: c.positioning.trim() } : {}),
      ...(c.strength.trim() ? { strength: c.strength.trim() } : {}),
      ...(c.weakness.trim() ? { weakness: c.weakness.trim() } : {}),
    }));
  out.content_pillars = state.content_pillars
    .filter((p) => p.name.trim().length > 0)
    .map((p) => ({
      name: p.name.trim(),
      ...(p.description.trim() ? { description: p.description.trim() } : {}),
      ...(p.cadence.trim() ? { cadence: p.cadence.trim() } : {}),
    }));
  out.tone_guidelines = {
    ...(state.tone_guidelines.voice.trim() ? { voice: state.tone_guidelines.voice.trim() } : {}),
    tone: stripEmptyStrings(state.tone_guidelines.tone),
    dos: stripEmptyStrings(state.tone_guidelines.dos),
    donts: stripEmptyStrings(state.tone_guidelines.donts),
  };
  out.channel_recommendations = state.channel_recommendations
    .filter((c) => c.channel.trim().length > 0)
    .map((c) => ({
      channel: c.channel.trim(),
      ...(c.role.trim() ? { role: c.role.trim() } : {}),
      ...(c.cadence.trim() ? { cadence: c.cadence.trim() } : {}),
      kpis: stripEmptyStrings(c.kpis),
    }));
  out.roadmap = state.roadmap
    .filter((r) => r.phase.trim().length > 0)
    .map((r) => ({
      phase: r.phase.trim(),
      ...(r.period.trim() ? { period: r.period.trim() } : {}),
      ...(r.focus.trim() ? { focus: r.focus.trim() } : {}),
      deliverables: stripEmptyStrings(r.deliverables),
    }));
  out.kpi_targets = state.kpi_targets
    .filter((k) => k.metric.trim().length > 0)
    .map((k) => ({
      metric: k.metric.trim(),
      ...(k.target.trim() ? { target: k.target.trim() } : {}),
      ...(k.period.trim() ? { period: k.period.trim() } : {}),
    }));
  out.dos_donts = {
    dos: stripEmptyStrings(state.dos_donts.dos),
    donts: stripEmptyStrings(state.dos_donts.donts),
  };

  const previousMeta =
    typeof original['source_metadata'] === 'object' && original['source_metadata'] !== null
      ? (original['source_metadata'] as Record<string, unknown>)
      : undefined;
  out.source_metadata = {
    generated_by: 'manual',
    edited_at: new Date().toISOString(),
    base_version_id: baseVersionId,
    base_version_number: baseVersionNumber,
    ...(previousMeta ? { base: previousMeta } : {}),
  };

  return out;
}

function stripEmptyObject(o: { statement: string; audience: string; alternative: string }) {
  return {
    ...(o.statement.trim() ? { statement: o.statement.trim() } : {}),
    ...(o.audience.trim() ? { audience: o.audience.trim() } : {}),
    ...(o.alternative.trim() ? { alternative: o.alternative.trim() } : {}),
  };
}

function stripEmptyStrings(arr: ReadonlyArray<string>): string[] {
  return arr.map((s) => s.trim()).filter((s) => s.length > 0);
}

function countPlaceholderOccurrences(state: EditableStrategy, summary: string): number {
  // Stringify everything once and count. Imperfect (an edited
  // string that contains the placeholder substring intentionally
  // still counts) but cheap and good enough for a soft warning.
  let count = 0;
  const haystacks: string[] = [summary];
  walkStrings(state, (s) => haystacks.push(s));
  for (const h of haystacks) {
    let i = 0;
    while ((i = h.indexOf(TEMPLATE_PLACEHOLDER, i)) !== -1) {
      count += 1;
      i += TEMPLATE_PLACEHOLDER.length;
    }
  }
  return count;
}

function walkStrings(value: unknown, visit: (s: string) => void): void {
  if (typeof value === 'string') {
    visit(value);
    return;
  }
  if (Array.isArray(value)) {
    for (const v of value) walkStrings(v, visit);
    return;
  }
  if (value && typeof value === 'object') {
    for (const v of Object.values(value as Record<string, unknown>)) walkStrings(v, visit);
  }
}
